import { IBlockDefinition, IBlockProps, RICH_TEXT_BLOCK_TYPE } from '@fox/blocks';
import BulletList from '@tiptap/extension-bullet-list';
import Link from '@tiptap/extension-link';
import OrderedList from '@tiptap/extension-ordered-list';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { generateHTML, JSONContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { readableColor } from 'polished';
import styled, { useTheme } from 'styled-components';
import { IField } from '../../form-root/contracts';
import { QuestionStyleTypes, useQuestionStyle } from '../../form-runtime-context';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { useConditionEvaluator } from '../form-block/conditionals/helpers';
import { ColorExt } from './tiptap-ext/custom-color-ext';
import { CustomFontFamily } from './tiptap-ext/custom-font-family-ext';
import { CustomListItem } from './tiptap-ext/custom-list-item-ext';
import { FontSizeExt } from './tiptap-ext/font-size-ext';
import { LineHeightExt } from './tiptap-ext/line-height-ext';

export const RichTextRuntimeWrap = styled.div<{ $textColor?: string }>`
	padding-top: 0px;
	padding-bottom: 0px;
	padding-left: 0px;
	padding-right: 0px;
	min-height: 0rem;
	border: none;
	border-radius: none;
	cursor: text;
	.ProseMirror:focus {
		border: none;
		outline: none;
	}
	& li > p {
		margin: 0;
	}
	& p.is-editor-empty:first-child::before {
		color: ${o => o.$textColor};
		content: attr(data-placeholder);
		font-size: 1rem;
		float: left;
		height: 0;
		pointer-events: none;
	}
`;

const RichTextBlock = ({ props }: IBlockProps<{ contents?: JSONContent }>) => {
	const theme = useTheme();
	const { backgroundColour } = useQuestionStyle(QuestionStyleTypes.general, {});
	const containerBackgroundColor = backgroundColour ?? '#FFFFFF';
	const textColor = readableColor(containerBackgroundColor, theme.palette.grey[700], theme.palette.grey[100], false);

	const htmlInstance = props.contents
		? generateHTML(props.contents, [
				StarterKit.configure({
					listItem: false,
					bulletList: false,
					orderedList: false
				}),
				BulletList.configure({
					keepMarks: true,
					keepAttributes: true
				}),
				OrderedList.configure({
					keepMarks: true,
					keepAttributes: true
				}),
				Underline,
				CustomListItem,
				TextAlign.configure({
					types: ['heading', 'paragraph', 'orderedList', 'bulletList']
				}),
				TextStyle,
				Placeholder.configure({
					emptyEditorClass: 'is-editor-empty',
					placeholder: 'Add text here..'
				}),
				ColorExt.configure({
					types: ['textStyle', 'listItem']
				}),
				FontSizeExt,
				LineHeightExt,
				Link.configure({
					openOnClick: false,
					HTMLAttributes: {
						target: '_blank'
					}
				}),
				CustomFontFamily
			])
		: '';

	return (
		<RichTextRuntimeWrap $textColor={textColor}>
			<div
				dangerouslySetInnerHTML={{
					__html: htmlInstance
				}}
			/>
		</RichTextRuntimeWrap>
	);
};

export const ConditionalRichTextBlock = (props: IBlockProps<IField & { contents?: JSONContent }>) => {
	useFormBlockReRenderer([], props.props);
	const shouldRender = useConditionEvaluator(props.props);
	if (!shouldRender) return null;

	return <RichTextBlock {...props} />;
};
export const ConditionalRichTextBlockRuntime: IBlockDefinition = {
	type: RICH_TEXT_BLOCK_TYPE,
	Component: ConditionalRichTextBlock
};
